import { showNewMessage } from '@intercom/messenger-js-sdk';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowRight, BadgeCheck, Check, Clock3, Wallet, Users } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { PageWrapper } from '@/components/layout/page-wrapper';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { useCurrentOrganization } from '@/features/organizations/hooks/useCurrentOrganization';
import {
    useBillingProducts,
    useCreateCheckoutSession,
    useOrganizationBilling
} from '@/features/organizations/hooks/useOrganizationBilling';
import { canManageOrganizationBilling } from '@/features/organizations/permissions';
import { hasExistingBillingSubscription } from '@/features/organizations/services/billing-source';
import { apiErrorStatusFallbackPresets, resolveApiErrorMessage } from '@/lib/api-error-translations';
import { redirectLifetimeBillingFromCheckoutRoute } from '@/lib/router-guards';
import {
    BASE_ACCOUNT_LIMIT,
    BASE_ACCOUNT_PRICE,
    EXTRA_ACCOUNT_PRICE,
    INCLUDED_FEATURES,
    MAX_ACCOUNTS,
    MIN_ACCOUNTS,
    calculateAdditionalAccounts,
    calculateMonthlyPrice,
    formatUsd
} from '@/features/pricing/pricing';

export const Route = createFileRoute('/_app/pricing')({
    beforeLoad: () => redirectLifetimeBillingFromCheckoutRoute(),
    component: PricingPage
});

const PricingPrimaryAction = ({
    isPlanCard = false,
    hasOrganization,
    hasSubscription,
    isCheckoutPending,
    isCheckoutDisabled,
    onCheckout
}: {
    isPlanCard?: boolean;
    hasOrganization: boolean;
    hasSubscription: boolean;
    isCheckoutPending: boolean;
    isCheckoutDisabled: boolean;
    onCheckout: () => void;
}) => {
    const { t } = useTranslation();
    const planCardProps = isPlanCard
        ? {
              size: 'lg' as const,
              className: 'w-full bg-sidebar-primary text-sidebar-primary-foreground hover:bg-sidebar-primary/90'
          }
        : {};

    if (!hasOrganization) {
        return (
            <Button asChild {...planCardProps}>
                <Link to='/organizations'>{t('pricing.primaryCta')}</Link>
            </Button>
        );
    }

    if (hasSubscription) {
        return (
            <Button asChild {...planCardProps}>
                <Link to='/settings'>{t('pricing.manageSubscriptionCta')}</Link>
            </Button>
        );
    }

    return (
        <Button type='button' onClick={onCheckout} disabled={isCheckoutDisabled} {...planCardProps}>
            {isCheckoutPending ? t('settings.billing.checkout.loading') : t('pricing.checkoutCta')}
        </Button>
    );
};

function PricingPage() {
    const { t } = useTranslation();
    const [accounts, setAccounts] = useState(MIN_ACCOUNTS);
    const { currentOrganization, currentOrganizationId } = useCurrentOrganization();
    const canStartCheckout = currentOrganization
        ? canManageOrganizationBilling(currentOrganization.membership.role)
        : false;
    const productsQuery = useBillingProducts();
    const billingQuery = useOrganizationBilling(currentOrganizationId);
    const checkoutMutation = useCreateCheckoutSession(currentOrganizationId);
    const hasSubscription = hasExistingBillingSubscription(billingQuery.data);
    const selectedProduct = useMemo(
        () => productsQuery.data?.find((product) => product.isActive) ?? productsQuery.data?.[0] ?? null,
        [productsQuery.data]
    );
    const isCheckoutDisabled =
        checkoutMutation.isPending ||
        productsQuery.isPending ||
        billingQuery.isPending ||
        !selectedProduct ||
        !canStartCheckout;
    let planDescription = t('pricing.requiresOrganization');

    if (currentOrganization) {
        if (hasSubscription) {
            planDescription = t('pricing.alreadySubscribed', {
                organizationName: currentOrganization.name
            });
        } else {
            planDescription = t('pricing.checkoutForOrganization', {
                organizationName: currentOrganization.name
            });
        }
    }

    const handleContactClick = () => {
        showNewMessage('');
    };

    const total = useMemo(() => calculateMonthlyPrice(accounts), [accounts]);
    const extraAccounts = useMemo(() => calculateAdditionalAccounts(accounts), [accounts]);
    const baseAccountCount = Math.min(accounts, BASE_ACCOUNT_LIMIT);
    const baseCost = BASE_ACCOUNT_PRICE;
    const additionalCost = extraAccounts * EXTRA_ACCOUNT_PRICE;

    async function handleCheckout(): Promise<void> {
        if (!currentOrganizationId) {
            return;
        }

        if (!canStartCheckout) {
            toast.error(t('apiErrors.billing.manageForbidden'));
            return;
        }

        if (!selectedProduct) {
            toast.error(t('apiErrors.billing.productsLoadFailed'));
            return;
        }

        try {
            const origin = window.location.origin;
            const response = await checkoutMutation.mutateAsync({
                productId: selectedProduct.id,
                input: {
                    successUrl: `${origin}/settings?tab=billing&billing=success`,
                    cancelUrl: `${origin}/settings?tab=billing&billing=cancel`,
                    interval: 'monthly',
                    quantity: accounts
                }
            });

            window.location.href = response.url;
        } catch (error) {
            toast.error(
                resolveApiErrorMessage(error, t, {
                    fallbackKey: 'apiErrors.billing.checkoutFailed',
                    statusFallbacks: apiErrorStatusFallbackPresets.billingCheckout
                })
            );
        }
    }

    return (
        <PageWrapper
            title={t('pricing.pageTitle')}
            className='gap-6'
            actions={
                <PricingPrimaryAction
                    hasOrganization={Boolean(currentOrganizationId)}
                    hasSubscription={hasSubscription}
                    isCheckoutPending={checkoutMutation.isPending}
                    isCheckoutDisabled={isCheckoutDisabled}
                    onCheckout={() => void handleCheckout()}
                />
            }
        >
            <div className='grid gap-6 xl:grid-cols-[1.1fr_0.9fr]'>
                <Card>
                    <CardHeader spacing='relaxed'>
                        <div className='flex flex-col gap-4 md:flex-row md:items-start md:justify-between'>
                            <div className='space-y-3'>
                                <Badge variant='secondary' className='w-fit'>
                                    {t('pricing.eyebrow')}
                                </Badge>
                                <div>
                                    <CardTitle size='heading'>{t('pricing.calculatorTitle')}</CardTitle>
                                    <CardDescription leading='relaxed' className='mt-2 max-w-2xl'>
                                        {t('pricing.calculatorDescription')}
                                    </CardDescription>
                                </div>
                            </div>
                            <div className='rounded-xl border bg-muted/40 px-4 py-3 text-left md:min-w-36 md:text-right'>
                                <div className='text-xs uppercase tracking-[0.18em] text-muted-foreground'>
                                    {t('pricing.accountsLabel')}
                                </div>
                                <div className='mt-1 text-3xl font-semibold text-foreground'>{accounts}</div>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent spacing='relaxed'>
                        <div className='rounded-xl border border-primary/20 bg-primary/5 p-5'>
                            <div className='flex items-start gap-3'>
                                <Clock3 className='mt-0.5 h-5 w-5 text-visible-accent-icon' />
                                <div>
                                    <p className='font-medium text-foreground'>{t('pricing.trialTitle')}</p>
                                    <p className='mt-1 text-sm text-muted-foreground'>
                                        {t('pricing.trialDescription')}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className='rounded-xl border bg-muted/20 p-5'>
                            <div className='flex items-center gap-2 text-foreground'>
                                <Label>
                                    <Users className='mr-2 inline h-4 w-4 text-visible-accent-icon' />
                                    {t('pricing.accountsIncludedLabel')}
                                </Label>
                            </div>
                            <div className='mt-6 space-y-4'>
                                <Slider
                                    value={[accounts]}
                                    min={MIN_ACCOUNTS}
                                    max={MAX_ACCOUNTS}
                                    step={1}
                                    onValueChange={(value) => setAccounts(value[0] ?? MIN_ACCOUNTS)}
                                    aria-label={t('pricing.accountsLabel')}
                                />
                                <div className='flex items-center justify-between text-sm text-muted-foreground'>
                                    <span>{t('pricing.minAccounts', { count: MIN_ACCOUNTS })}</span>
                                    <span>{t('pricing.maxAccounts', { count: MAX_ACCOUNTS })}</span>
                                </div>
                            </div>
                        </div>

                        <div className='grid gap-4 md:grid-cols-3'>
                            <div className='rounded-xl border bg-background p-4'>
                                <div className='text-sm text-muted-foreground'>
                                    {t('pricing.firstTierLabel', { count: BASE_ACCOUNT_LIMIT })}
                                </div>
                                <div className='mt-2 text-2xl font-semibold'>{formatUsd(baseCost)}</div>
                                <p className='mt-1 text-sm text-muted-foreground'>
                                    {t('pricing.firstTierRate', {
                                        price: formatUsd(BASE_ACCOUNT_PRICE),
                                        count: BASE_ACCOUNT_LIMIT
                                    })}
                                </p>
                            </div>
                            <div className='rounded-xl border bg-background p-4'>
                                <div className='text-sm text-muted-foreground'>
                                    {t('pricing.additionalAccountsLabel')}
                                </div>
                                <div className='mt-2 text-2xl font-semibold'>{extraAccounts}</div>
                                <p className='mt-1 text-sm text-muted-foreground'>
                                    {t('pricing.additionalAccountsRate', {
                                        price: formatUsd(EXTRA_ACCOUNT_PRICE),
                                        count: BASE_ACCOUNT_LIMIT
                                    })}
                                </p>
                            </div>
                            <div className='rounded-xl border bg-background p-4'>
                                <div className='text-sm text-muted-foreground'>{t('pricing.monthlyTotalLabel')}</div>
                                <div className='mt-2 text-2xl font-semibold text-primary'>{formatUsd(total)}</div>
                            </div>
                        </div>
                    </CardContent>
                    <CardFooter>
                        <div className='flex w-full flex-col gap-3 rounded-xl border bg-secondary/10 px-5 py-4 text-sm sm:flex-row sm:items-center sm:justify-between'>
                            <div>
                                <p className='font-medium text-foreground'>{t('pricing.customSolutionTitle')}</p>
                                <p className='text-muted-foreground'>{t('pricing.customSolutionDescription')}</p>
                            </div>
                            <Button type='button' variant='secondary' className='shrink-0' onClick={handleContactClick}>
                                {t('pricing.contactCta')} <ArrowRight className='h-4 w-4' />
                            </Button>
                        </div>
                    </CardFooter>
                </Card>

                <Card variant='sidebar'>
                    <CardHeader spacing='relaxed'>
                        <div className='flex items-start justify-between gap-3'>
                            <div>
                                <CardTitle size='heading' tone='sidebar'>
                                    {t('pricing.planTitle')}
                                </CardTitle>
                                <CardDescription tone='sidebar' className='mt-2'>
                                    {planDescription}
                                </CardDescription>
                            </div>
                            <Wallet className='h-5 w-5 text-visible-sidebar-icon' />
                        </div>
                        <div className='space-y-2'>
                            <div className='flex items-end gap-2'>
                                <span className='text-5xl font-semibold tracking-tight'>{formatUsd(total)}</span>
                                <span className='pb-1 text-sm text-sidebar-foreground/70'>/ {t('pricing.month')}</span>
                            </div>
                            <div className='inline-flex items-center gap-2 rounded-full border border-sidebar-primary/30 bg-sidebar-accent px-3 py-1 text-sm'>
                                <BadgeCheck className='h-4 w-4 text-visible-sidebar-icon' />
                                {t('pricing.accountsSelected', { count: accounts })}
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent spacing='relaxed'>
                        <div className='rounded-xl border border-sidebar-border bg-sidebar-accent p-5'>
                            <div className='space-y-4'>
                                <div className='flex items-start justify-between gap-4'>
                                    <div>
                                        <p className='font-medium'>
                                            {t('pricing.planFirstTierLine', { count: baseAccountCount })}
                                        </p>
                                        <p className='text-sm text-sidebar-foreground/70'>
                                            {t('pricing.firstTierRate', {
                                                price: formatUsd(BASE_ACCOUNT_PRICE),
                                                count: BASE_ACCOUNT_LIMIT
                                            })}
                                        </p>
                                    </div>
                                    <p className='font-semibold'>{formatUsd(baseCost)}</p>
                                </div>
                                <div className='flex items-start justify-between gap-4'>
                                    <div>
                                        <p className='font-medium'>{t('pricing.additionalAccountsLabel')}</p>
                                        <p className='text-sm text-sidebar-foreground/70'>
                                            {t('pricing.planTierCalculation', {
                                                price: formatUsd(EXTRA_ACCOUNT_PRICE),
                                                count: extraAccounts
                                            })}
                                        </p>
                                    </div>
                                    <p className='font-semibold'>{formatUsd(additionalCost)}</p>
                                </div>
                            </div>
                        </div>
                        <div className='space-y-3'>
                            <p className='text-sm font-medium uppercase tracking-[0.2em] text-sidebar-foreground/70'>
                                {t('pricing.includedLabel')}
                            </p>
                            <ul className='space-y-3'>
                                {INCLUDED_FEATURES.map((feature) => (
                                    <li key={feature} className='flex items-start gap-3 text-sm'>
                                        <span className='mt-0.5 rounded-full bg-sidebar-primary/15 p-1 text-visible-sidebar-icon'>
                                            <Check className='h-3.5 w-3.5' />
                                        </span>
                                        <span>{t(feature)}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </CardContent>
                    <CardFooter direction='column'>
                        <PricingPrimaryAction
                            isPlanCard
                            hasOrganization={Boolean(currentOrganizationId)}
                            hasSubscription={hasSubscription}
                            isCheckoutPending={checkoutMutation.isPending}
                            isCheckoutDisabled={isCheckoutDisabled}
                            onCheckout={() => void handleCheckout()}
                        />
                    </CardFooter>
                </Card>
            </div>
        </PageWrapper>
    );
}
